import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { DatePicker } from '../ui/DatePicker';
import { useNotifications } from '../../contexts/NotificationContext';
import { OperativeActivity, getStepTitlesForActivity, operativeActivitiesStore } from '../../services/operativeActivities';
import { CheckCircle2, Circle, FileText, Settings2 } from 'lucide-react';
interface EditActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: OperativeActivity | null;
  onSave: () => void;
}
export function EditActivityModal({
  isOpen,
  onClose,
  activity,
  onSave
}: EditActivityModalProps) {
  const {
    addNotification
  } = useNotifications();
  const [activeTab, setActiveTab] = useState<string>('generali');
  // General Data State
  const [title, setTitle] = useState('');
  const [operator, setOperator] = useState('');
  const [deadline, setDeadline] = useState('');
  const [product, setProduct] = useState('');
  // Steps Data State
  const [stepsCompleted, setStepsCompleted] = useState<boolean[]>([]);
  const [customStepNames, setCustomStepNames] = useState<string[]>([]);
  useEffect(() => {
    if (activity && isOpen) {
      setTitle(activity.title);
      setOperator(activity.operator);
      setDeadline(activity.deadline);
      setProduct(activity.product);
      setStepsCompleted([...activity.stepsCompleted]);
      if (activity.activityType === 'personalizzata' && activity.customStepNames) {
        setCustomStepNames([...activity.customStepNames]);
      } else {
        setCustomStepNames([]);
      }
      setActiveTab('generali');
    }
  }, [activity, isOpen]);
  if (!activity) return null;
  const stepTitles = getStepTitlesForActivity(activity);
  const isCustom = activity.activityType === 'personalizzata';
  const handleSaveGeneral = () => {
    operativeActivitiesStore.updateActivity(activity.id, {
      title,
      operator,
      deadline,
      product
    });
    addNotification('success', 'Modifiche salvate', 'Dati generali aggiornati con successo.');
    onSave();
  };
  const handleSaveStep = (index: number) => {
    // For custom activities, we might have updated the step name
    const updates: Partial<OperativeActivity> = {
      stepsCompleted: [...stepsCompleted]
    };
    if (isCustom) {
      updates.customStepNames = [...customStepNames];
    }
    operativeActivitiesStore.updateActivity(activity.id, updates);
    addNotification('success', 'Modifiche salvate', `Step "${isCustom ? customStepNames[index] : stepTitles[index]}" aggiornato con successo.`);
    onSave();
  };
  const handleToggleStep = (index: number) => {
    const newCompleted = [...stepsCompleted];
    newCompleted[index] = !newCompleted[index];
    // If unchecking a step, uncheck all subsequent steps
    if (!newCompleted[index]) {
      for (let i = index + 1; i < newCompleted.length; i++) {
        newCompleted[i] = false;
      }
    }
    // If checking a step, check all previous steps
    else {
      for (let i = 0; i < index; i++) {
        newCompleted[i] = true;
      }
    }
    setStepsCompleted(newCompleted);
  };
  const handleCustomStepNameChange = (index: number, newName: string) => {
    const newNames = [...customStepNames];
    newNames[index] = newName;
    setCustomStepNames(newNames);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Modifica Attività: ${activity.code || activity.title}`} size="2xl" data-id="element-481">
      <div className="flex flex-col md:flex-row gap-6 min-h-[500px]" data-id="element-482">
        {/* Sidebar */}
        <div className="w-full md:w-64 flex-shrink-0 border-r border-slate-100 pr-6" data-id="element-483">
          <div className="space-y-1" data-id="element-484">
            <button onClick={() => setActiveTab('generali')} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'generali' ? 'bg-seform-orange/10 text-seform-orange' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-485">
              <Settings2 className="h-4 w-4" data-id="element-486" />
              Dati Generali
            </button>

            <div className="pt-4 pb-2" data-id="element-487">
              <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-488">
                Step Operativi
              </p>
            </div>

            {stepTitles.map((stepName, index) => {
            const isCompleted = stepsCompleted[index];
            const displayTitle = isCustom && customStepNames[index] ? customStepNames[index] : stepName;
            return <button key={index} onClick={() => setActiveTab(`step-${index}`)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeTab === `step-${index}` ? 'bg-seform-orange/10 text-seform-orange' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-489">
                  {isCompleted ? <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" data-id="element-490" /> : <Circle className="h-4 w-4 text-slate-300 flex-shrink-0" data-id="element-491" />}
                  <span className="truncate" data-id="element-492">{displayTitle}</span>
                </button>;
          })}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 pl-2" data-id="element-493">
          {activeTab === 'generali' && <div className="space-y-6 animate-in fade-in duration-200" data-id="element-494">
              <div className="flex items-center justify-between mb-6" data-id="element-495">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" data-id="element-496">
                  <FileText className="h-5 w-5 text-slate-400" data-id="element-497" />
                  Dati Generali
                </h3>
                <Badge variant={activity.activityType === 'standard' ? 'info' : 'warning'} data-id="element-498">
                  {activity.activityType === 'standard' ? 'Standard' : 'Personalizzata'}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-499">
                <Input label="Titolo attività" value={title} onChange={e => setTitle(e.target.value)} className="col-span-full" data-id="element-500" />

                <Select label="Prodotto" value={product} onChange={e => setProduct(e.target.value)} options={[{
              value: 'Consulenza Sicurezza',
              label: 'Consulenza Sicurezza'
            }, {
              value: 'HACCP Base',
              label: 'HACCP Base'
            }, {
              value: 'Privacy GDPR',
              label: 'Privacy GDPR'
            }, {
              value: 'DVR',
              label: 'DVR'
            }, {
              value: 'Formazione Lavoratori',
              label: 'Formazione Lavoratori'
            }, {
              value: 'Consulenza Ambiente',
              label: 'Consulenza Ambiente'
            }, {
              value: 'Antiriciclaggio',
              label: 'Antiriciclaggio'
            }]} data-id="element-501" />

                <Select label="Operatore assegnato" value={operator} onChange={e => setOperator(e.target.value)} options={[{
              value: 'Alessio',
              label: 'Alessio'
            }, {
              value: 'Lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'Gian Franco',
              label: 'Gian Franco'
            }, {
              value: 'Tiziano',
              label: 'Tiziano'
            }]} data-id="element-502" />

                <DatePicker label="Scadenza" value={deadline} onChange={e => setDeadline(e.target.value)} data-id="element-503" />

                <Input label="Cliente" value={activity.clientName} disabled className="bg-slate-50" data-id="element-504" />

                <Input label="Contratto" value={activity.contractNumber} disabled className="bg-slate-50" data-id="element-505" />
              </div>

              <div className="pt-6 mt-6 border-t border-slate-100 flex justify-end" data-id="element-506">
                <Button onClick={handleSaveGeneral} data-id="element-507">
                  Salva modifiche generali
                </Button>
              </div>
            </div>}

          {activeTab.startsWith('step-') && <div className="space-y-6 animate-in fade-in duration-200" data-id="element-508">
              {(() => {
            const stepIndex = parseInt(activeTab.split('-')[1]);
            const isCompleted = stepsCompleted[stepIndex];
            const displayTitle = isCustom && customStepNames[stepIndex] ? customStepNames[stepIndex] : stepTitles[stepIndex];
            return <>
                    <div className="flex items-center justify-between mb-6" data-id="element-509">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" data-id="element-510">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-bold text-slate-500" data-id="element-511">
                          {stepIndex + 1}
                        </span>
                        Dettaglio Step
                      </h3>
                      <Badge variant={isCompleted ? 'success' : 'warning'} data-id="element-512">
                        {isCompleted ? 'Completato' : 'Da completare'}
                      </Badge>
                    </div>

                    <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 space-y-6" data-id="element-513">
                      {isCustom ? <Input label="Nome Step" value={customStepNames[stepIndex] || ''} onChange={e => handleCustomStepNameChange(stepIndex, e.target.value)} placeholder="Inserisci il nome dello step..." data-id="element-514" /> : <div data-id="element-515">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-516">
                            Nome Step
                          </label>
                          <p className="text-slate-900 font-medium p-3 bg-white rounded-lg border border-slate-200" data-id="element-517">
                            {displayTitle}
                          </p>
                        </div>}

                      <div className="pt-4 border-t border-slate-200" data-id="element-518">
                        <label className="flex items-center gap-3 cursor-pointer group" data-id="element-519">
                          <div className="relative flex items-center justify-center" data-id="element-520">
                            <input type="checkbox" className="peer sr-only" checked={isCompleted} onChange={() => handleToggleStep(stepIndex)} data-id="element-521" />
                            <div className="w-6 h-6 rounded border-2 border-slate-300 peer-checked:border-seform-orange peer-checked:bg-seform-orange transition-colors group-hover:border-seform-orange" data-id="element-522"></div>
                            <CheckCircle2 className="absolute w-4 h-4 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" data-id="element-523" />
                          </div>
                          <div data-id="element-524">
                            <p className="text-sm font-medium text-slate-900" data-id="element-525">
                              Segna come completato
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-526">
                              {isCompleted ? 'Questo step è stato completato. Deselezionando, verranno deselezionati anche gli step successivi.' : 'Selezionando questo step, verranno automaticamente completati anche gli step precedenti.'}
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="pt-6 mt-6 border-t border-slate-100 flex justify-end" data-id="element-527">
                      <Button onClick={() => handleSaveStep(stepIndex)} data-id="element-528">
                        Salva modifiche step
                      </Button>
                    </div>
                  </>;
          })()}
            </div>}
        </div>
      </div>
    </Modal>;
}